'use client';

import { c as _c } from "react/compiler-runtime";
import { decodeColor, formatCents } from 'betterplace-utils';
import { AnimateNumber } from 'design-system/client';
import { useTranslations, useLocale } from 'next-intl';
import type { CSSProperties, ReactNode } from 'react';
import WidgetHeadline from '../WidgetHeadline';
import { DONATION_AMOUNT_ANIMATION_LENGTH, DURATION, GIF_HEIGHT, SOUND_VOLUME } from './constants';
import type { MilestoneDetailsProps, MilestoneProps } from './types';
import GifAndSound from '@/components/GifAndSound';
import styles from './Milestone.module.css';
import { DEFAULT_MILESTONE_ALERT } from './getDefaultValues';
import defaultMilestoneAnimation from './default_milestone_animation.gif';
function CelebrationText(t0) {
  const $ = _c(9);
  const {
    children
  } = t0;
  const t = useTranslations("nextjs.streambot.live_widgets.milestones");
  let t1;
  if ($[0] !== t) {
    t1 = t("default_animation_text");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div>{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children) {
    t3 = <div data-testid="amount">{children}</div>;
    $[4] = children;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className={styles.celebration}>{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function MilestoneDetails(t0) {
  const $ = _c(26);
  const {
    headlineFont,
    headlineFontWeight,
    headlineFontColor,
    headlineFontSize,
    dataFont,
    dataFontWeight,
    dataFontSize,
    dataFontColor,
    amountInCents,
    lastMilestoneReached
  } = t0;
  const locale = useLocale();
  const t = useTranslations("nextjs.streambot.live_widgets");
  let t1;
  if ($[0] !== t) {
    t1 = t("milestones.next_milestone");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== headlineFont || $[3] !== headlineFontColor || $[4] !== headlineFontSize || $[5] !== headlineFontWeight || $[6] !== t1) {
    t2 = <WidgetHeadline content={t1} headlineFont={headlineFont} headlineFontWeight={headlineFontWeight} headlineFontColor={headlineFontColor} headlineFontSize={headlineFontSize} />;
    $[2] = headlineFont;
    $[3] = headlineFontColor;
    $[4] = headlineFontSize;
    $[5] = headlineFontWeight;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const t3 = `${dataFontSize}px`;
  let t4;
  if ($[8] !== dataFontColor) {
    t4 = decodeColor(dataFontColor?.toString());
    $[8] = dataFontColor;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== dataFont || $[11] !== dataFontWeight || $[12] !== t3 || $[13] !== t4) {
    t5 = {
      "--streambot-milestones-data-font": dataFont,
      "--streambot-milestones-data-font-weight": dataFontWeight,
      "--streambot-milestones-data-font-size": t3,
      "--streambot-milestones-data-font-color": t4
    };
    $[10] = dataFont;
    $[11] = dataFontWeight;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const t6 = t5 as CSSProperties;
  let t7;
  if ($[15] !== amountInCents || $[16] !== lastMilestoneReached || $[17] !== locale || $[18] !== t) {
    t7 = lastMilestoneReached ? <div>{t("milestones.all_milestones_reached")}</div> : <AnimateNumber animationDurationInMs={DONATION_AMOUNT_ANIMATION_LENGTH} maxNumberScale={1.3}>{formatCents(locale, amountInCents)}</AnimateNumber>;
    $[15] = amountInCents;
    $[16] = lastMilestoneReached;
    $[17] = locale;
    $[18] = t;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t6 || $[21] !== t7) {
    t8 = <div className="milestones" style={t6}>{t7}</div>;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t2 || $[24] !== t8) {
    t9 = <>{t2}{t8}</>;
    $[23] = t2;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
}
function Milestone(t0) {
  const $ = _c(30);
  const {
    onPlaybackStart,
    headlineFont,
    headlineFontColor,
    headlineFontSize,
    headlineFontWeight,
    dataFont,
    dataFontColor,
    dataFontSize,
    dataFontWeight,
    soundUrl,
    gifUrl: gifUrl_,
    amountInCents,
    id,
    onPlaybackComplete,
    skipPlayback,
    showMilestoneDetails: t1,
    lastMilestoneReached: t2
  } = t0;
  const showMilestoneDetails = t1 === undefined ? true : t1;
  const lastMilestoneReached = t2 === undefined ? false : t2;
  const locale = useLocale();
  const milestoneHasSound = !!soundUrl;
  const gifUrl = gifUrl_ === DEFAULT_MILESTONE_ALERT ? defaultMilestoneAnimation.src : gifUrl_;
  let t3;
  if ($[0] !== amountInCents || $[1] !== dataFont || $[2] !== dataFontColor || $[3] !== dataFontSize || $[4] !== dataFontWeight || $[5] !== headlineFont || $[6] !== headlineFontColor || $[7] !== headlineFontSize || $[8] !== headlineFontWeight || $[9] !== lastMilestoneReached || $[10] !== showMilestoneDetails) {
    t3 = showMilestoneDetails ? <MilestoneDetails amountInCents={amountInCents} dataFont={dataFont} dataFontColor={dataFontColor} dataFontSize={dataFontSize} dataFontWeight={dataFontWeight} headlineFont={headlineFont} headlineFontColor={headlineFontColor} headlineFontSize={headlineFontSize} headlineFontWeight={headlineFontWeight} lastMilestoneReached={lastMilestoneReached} /> : null;
    $[0] = amountInCents;
    $[1] = dataFont;
    $[2] = dataFontColor;
    $[3] = dataFontSize;
    $[4] = dataFontWeight;
    $[5] = headlineFont;
    $[6] = headlineFontColor;
    $[7] = headlineFontSize;
    $[8] = headlineFontWeight;
    $[9] = lastMilestoneReached;
    $[10] = showMilestoneDetails;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const t4 = `${skipPlayback}`;
  let t5;
  if ($[12] !== amountInCents || $[13] !== locale) {
    t5 = formatCents(locale, amountInCents);
    $[12] = amountInCents;
    $[13] = locale;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t5) {
    t6 = <CelebrationText>{t5}</CelebrationText>;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== gifUrl || $[18] !== id || $[19] !== milestoneHasSound || $[20] !== onPlaybackComplete || $[21] !== onPlaybackStart || $[22] !== skipPlayback || $[23] !== soundUrl || $[24] !== t4 || $[25] !== t6) {
    t7 = <div className="milestonesGif"><GifAndSound id={id} duration={DURATION} soundSrc={soundUrl} volume={SOUND_VOLUME} playWithSound={milestoneHasSound} onPlaybackComplete={onPlaybackComplete} onPlaybackStart={onPlaybackStart} gifSrc={gifUrl} gifHeight={GIF_HEIGHT} skipPlayback={skipPlayback} key={t4}>{t6}</GifAndSound></div>;
    $[17] = gifUrl;
    $[18] = id;
    $[19] = milestoneHasSound;
    $[20] = onPlaybackComplete;
    $[21] = onPlaybackStart;
    $[22] = skipPlayback;
    $[23] = soundUrl;
    $[24] = t4;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== t3 || $[28] !== t7) {
    t8 = <div><div className="list milestonesContent">{t3}{t7}</div></div>;
    $[27] = t3;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  return t8;
}
export default Milestone;